import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { api, setTokens, clearTokens, getRefreshToken } from '@/utils/api';
import { Currency } from '@yaatou/types';

interface User {
  id: string;
  email: string;
  name: string;
  avatar: string | null;
  role: 'customer' | 'admin' | 'super_admin';
  preferredCurrency?: Currency;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User['role']>;
  register: (name: string, email: string, password: string) => Promise<User['role']>;
  loginWithGoogle: (idToken: string) => Promise<User['role']>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getRedirectPath: (role?: string) => string;
  refreshTokens: (accessToken: string, refreshToken: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

const STORAGE_KEY = 'yaatou_auth_user';
const TOKEN_KEY = 'yaatou_access_token';
const REFRESH_TOKEN_KEY = 'yaatou_refresh_token';

function buildName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

function normalizeRole(role?: string): User['role'] {
  const r = (role || '').toLowerCase();
  if (r === 'admin') return 'admin';
  if (r === 'super_admin' || r === 'superadmin') return 'super_admin';
  return 'customer';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const saveUser = useCallback((u: User | null) => {
    setUser(u);
    if (u) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const refreshTokens = useCallback((accessToken: string, refreshToken: string) => {
    setTokens(accessToken, refreshToken);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get<{ id: string; email: string; firstName: string; lastName: string; role: string; preferredCurrency?: string }>('/users/me');
      if (!data?.firstName) {
        clearTokens();
        saveUser(null);
        return;
      }
      const fullName = buildName(data.firstName, data.lastName);
      saveUser({
        id: data.id,
        email: data.email,
        name: fullName,
        avatar: null,
        role: normalizeRole(data.role),
        preferredCurrency: data.preferredCurrency as Currency | undefined,
      });
    } catch {
      clearTokens();
      saveUser(null);
    }
  }, [saveUser]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const accessToken = localStorage.getItem(TOKEN_KEY);
      const refreshToken = getRefreshToken();
      if (!accessToken || !refreshToken) {
        clearInterval(interval);
        return;
      }

      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const exp = payload.exp;
        const now = Math.floor(Date.now() / 1000);
        if (exp - now < 120 && exp - now > 0) {
          await refreshAccessTokenSilently();
        } else if (exp - now <= 0) {
          clearTokens();
          saveUser(null);
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshUser, saveUser]);

  const refreshAccessTokenSilently = async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return;
      const response = await fetch(`${__API_URL__}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-slug': 'yaatou-senegal',
        },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        clearTokens();
        saveUser(null);
        return;
      }
      const data = await response.json();
      const newAccessToken = data?.data?.accessToken || data?.accessToken;
      const newRefreshToken = data?.data?.refreshToken || data?.refreshToken;
      if (newAccessToken) {
        setTokens(newAccessToken, newRefreshToken || refreshToken);
      }
    } catch {
      clearTokens();
      saveUser(null);
    }
  };

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const data = await api.post<{
          user: { id: string; email: string; firstName: string; lastName: string; role: string; preferredCurrency?: string };
          tokens: { accessToken: string; refreshToken: string };
        }>('/auth/login', {
          email: email.toLowerCase().trim(),
          password,
        });
        const role = normalizeRole(data.user.role);
        const fullName = buildName(data.user.firstName, data.user.lastName);
        setTokens(data.tokens.accessToken, data.tokens.refreshToken);
        saveUser({
          id: data.user.id,
          email: data.user.email,
          name: fullName,
          avatar: null,
          role,
          preferredCurrency: data.user.preferredCurrency as Currency | undefined,
        });
        return role;
      } finally {
        setIsLoading(false);
      }
    },
    [saveUser],
  );

  const register = useCallback(
    async (name: string, email: string, password: string) => {
      setIsLoading(true);
      try {
        const [firstName, ...lastParts] = name.split(' ');
        const lastName = lastParts.join(' ') || '';
        const data = await api.post<{
          user: { id: string; email: string; firstName: string; lastName: string; role: string; preferredCurrency?: string };
          tokens: { accessToken: string; refreshToken: string };
        }>('/auth/register', {
          firstName,
          lastName,
          email: email.toLowerCase().trim(),
          password,
        });
        const role = normalizeRole(data.user.role);
        const fullName = buildName(data.user.firstName, data.user.lastName);
        setTokens(data.tokens.accessToken, data.tokens.refreshToken);
        saveUser({
          id: data.user.id,
          email: data.user.email,
          name: fullName,
          avatar: null,
          role,
          preferredCurrency: data.user.preferredCurrency as Currency | undefined,
        });
        return role;
      } finally {
        setIsLoading(false);
      }
    },
    [saveUser],
  );

  const loginWithGoogle = useCallback(async (idToken: string) => {
    setIsLoading(true);
    try {
      const data = await api.post<{
        user: { id: string; email: string; firstName: string; lastName: string; role: string; preferredCurrency?: string };
        tokens: { accessToken: string; refreshToken: string };
      }>('/auth/google', { idToken });
      const role = normalizeRole(data.user.role);
      const fullName = buildName(data.user.firstName, data.user.lastName);
      setTokens(data.tokens.accessToken, data.tokens.refreshToken);
      saveUser({
        id: data.user.id,
        email: data.user.email,
        name: fullName,
        avatar: null,
        role,
        preferredCurrency: data.user.preferredCurrency as Currency | undefined,
      });
      return role;
    } finally {
      setIsLoading(false);
    }
  }, [saveUser]);

  const getRedirectPath = useCallback(
    (role?: string) => {
      const normalized = normalizeRole(role);
      if (normalized === 'super_admin') return '/super-admin';
      if (normalized === 'admin') return '/admin';
      return '/explore';
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', {});
    } catch {
      // ignore logout errors
    } finally {
      clearTokens();
      saveUser(null);
    }
  }, [saveUser]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    loginWithGoogle,
    logout,
    refreshUser,
    getRedirectPath,
    refreshTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
